import { useEffect, useState, useCallback } from 'react'
import { LoadingState } from '@/components/shared/LoadingState'
import { BeltVisualizer } from '@/components/account/BeltVisualizer'
import { useAccount } from '@/hooks/useAccount'
import { useUpdateAccount } from '@/hooks/useUpdateAccount'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Belt, UpdateAccountDto } from '@/types/account'

/**
 * Account page component for viewing and editing user account information.
 *
 * States:
 * - Loading: While fetching account data
 * - Error: When account data fails to load (with retry)
 * - Read-only: Displaying account information
 * - Edit: Form for editing account fields
 * - Success: Feedback after successful save (via toast)
 */
export function AccountPage() {
  const { data: account, isLoading, isError, error, refetch } = useAccount()
  const updateMutation = useUpdateAccount()

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [formData, setFormData] = useState<UpdateAccountDto>({})
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  // Initialize form data when account loads
  useEffect(() => {
    if (account) {
      setFormData({
        firstName: account.firstName,
        lastName: account.lastName,
        belt: account.belt,
        rank: account.rank,
      })
    }
  }, [account])

  // Define handleCancel before useEffect that uses it
  const handleCancel = useCallback(() => {
    // Reset form data to original values
    if (account) {
      setFormData({
        firstName: account.firstName,
        lastName: account.lastName,
        belt: account.belt,
        rank: account.rank,
      })
    }
    setValidationErrors({})
    setIsEditMode(false)
  }, [account])

  // Keyboard support: ESC key to cancel edit mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditMode) {
        handleCancel()
      }
    }

    if (isEditMode) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isEditMode, handleCancel])

  // Edit mode handlers
  const handleEdit = () => {
    setIsEditMode(true)
  }

  const handleSave = async () => {
    const { isValid, errors } = validateForm(formData)
    if (!isValid) {
      const firstErrorField = Object.keys(errors)[0]
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus()
      }
      return
    }

    // Trim whitespace from text fields
    const trimmedData: UpdateAccountDto = {
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName?.trim(),
      belt: formData.belt || null,
      rank: formData.rank || null,
    }

    try {
      await updateMutation.mutateAsync(trimmedData)
      setIsEditMode(false)
      setValidationErrors({})
    } catch (err) {
      console.error('Save failed:', err)
    }
  }

  const handleFieldChange = (
    field: keyof UpdateAccountDto,
    value: string | null
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Validation function
  const validateForm = (
    data: UpdateAccountDto = formData
  ): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {}

    // First Name validation
    if (data.firstName) {
      const trimmed = data.firstName.trim()
      if (trimmed.length === 0) {
        errors.firstName = 'First name is required'
      } else if (trimmed.length > 100) {
        errors.firstName = 'First name must be 100 characters or less'
      }
    }

    // Last Name validation
    if (data.lastName) {
      const trimmed = data.lastName.trim()
      if (trimmed.length === 0) {
        errors.lastName = 'Last name is required'
      } else if (trimmed.length > 100) {
        errors.lastName = 'Last name must be 100 characters or less'
      }
    }

    setValidationErrors(errors)
    return { isValid: Object.keys(errors).length === 0, errors }
  }

  // Check if there are unsaved changes
  const hasChanges =
    account &&
    (formData.firstName !== account.firstName ||
      formData.lastName !== account.lastName ||
      formData.belt !== account.belt ||
      formData.rank !== account.rank)

  // Loading State: Show loading indicator while data fetches
  if (isLoading) {
    return <LoadingState message="Loading account information..." />
  }

  // Error State: Show error message with retry button
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
        <div className="mx-auto max-w-md p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-black">
            Failed to load account
          </h2>
          <p className="mb-4 text-[#666666]">
            {error instanceof Error
              ? error.message
              : 'An unexpected error occurred'}
          </p>
          <Button onClick={() => refetch()} className="bg-[#0033A0] text-white">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // Safety check: Account data should exist if not loading or error
  if (!account) {
    return null
  }

  // Belt label mapping
  const beltLabels: Record<Belt, string> = {
    WHITE: 'White Belt',
    BLUE: 'Blue Belt',
    PURPLE: 'Purple Belt',
    BROWN: 'Brown Belt',
    BLACK: 'Black Belt',
  }

  // Rank label mapping (0-4 stripes)
  const rankLabel = account.rank
    ? `${account.rank} ${Number(account.rank) === 1 ? 'Stripe' : 'Stripes'}`
    : 'No Stripes'

  // Read-only State: Display all account fields with modern card layout
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* Edit Mode Alert */}
        {isEditMode && (
          <div className="rounded-lg border-l-4 border-[#0033A0] bg-[#0033A0]/5 px-4 py-3">
            <p className="flex items-center text-sm text-[#0033A0]">
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              You are currently editing your profile. Changes won't be saved
              until you click Save.
            </p>
          </div>
        )}

        {/* Personal Information Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0033A0]/10">
                <svg
                  className="h-5 w-5 text-[#0033A0]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-black">
                Personal Information
              </h2>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={!hasChanges || updateMutation.isPending}
                    className="bg-[#0033A0] hover:bg-[#002080]"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={handleEdit}
                  className="bg-[#0033A0] hover:bg-[#002080]"
                >
                  Edit
                </Button>
              )}
            </div>
          </div>

          <div className="md:grid-cols-2 grid grid-cols-1 gap-4">
            {/* First Name */}
            <div>
              <label
                htmlFor="firstName"
                className="mb-1 flex items-center gap-2 text-sm font-medium text-[#666666]"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                First Name
              </label>
              {isEditMode ? (
                <>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName || ''}
                    onChange={e =>
                      handleFieldChange('firstName', e.target.value)
                    }
                    placeholder="Enter first name"
                    aria-describedby={
                      validationErrors.firstName ? 'firstName-error' : undefined
                    }
                    aria-invalid={!!validationErrors.firstName}
                  />
                  {validationErrors.firstName && (
                    <p
                      id="firstName-error"
                      className="mt-1 text-sm text-[#D62828]"
                    >
                      {validationErrors.firstName}
                    </p>
                  )}
                  {formData.firstName && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {formData.firstName.length}/100
                    </span>
                  )}
                </>
              ) : (
                <div className="text-base text-black">
                  {account.firstName || '—'}
                </div>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label
                htmlFor="lastName"
                className="mb-1 flex items-center gap-2 text-sm font-medium text-[#666666]"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Last Name
              </label>
              {isEditMode ? (
                <>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName || ''}
                    onChange={e =>
                      handleFieldChange('lastName', e.target.value)
                    }
                    placeholder="Enter last name"
                    aria-describedby={
                      validationErrors.lastName ? 'lastName-error' : undefined
                    }
                    aria-invalid={!!validationErrors.lastName}
                  />
                  {validationErrors.lastName && (
                    <p
                      id="lastName-error"
                      className="mt-1 text-sm text-[#D62828]"
                    >
                      {validationErrors.lastName}
                    </p>
                  )}
                  {formData.lastName && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {formData.lastName.length}/100
                    </span>
                  )}
                </>
              ) : (
                <div className="text-base text-black">
                  {account.lastName || '—'}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* BJJ Progression Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6B3FA0]/10">
              <svg
                className="h-5 w-5 text-[#6B3FA0]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-black">
              BJJ Progression
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
            <div className="space-y-4">
              {/* Belt */}
              <div>
                <label
                  htmlFor="belt"
                  className="mb-1 flex items-center gap-2 text-sm font-medium text-[#666666]"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  Belt
                </label>
                {isEditMode ? (
                  <Select
                    value={formData.belt || 'NONE'}
                    onValueChange={value =>
                      handleFieldChange('belt', value === 'NONE' ? null : value)
                    }
                  >
                    <SelectTrigger id="belt" className="max-w-md">
                      <SelectValue placeholder="Select belt" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">Not set</SelectItem>
                      <SelectItem value="WHITE">White Belt</SelectItem>
                      <SelectItem value="BLUE">Blue Belt</SelectItem>
                      <SelectItem value="PURPLE">Purple Belt</SelectItem>
                      <SelectItem value="BROWN">Brown Belt</SelectItem>
                      <SelectItem value="BLACK">Black Belt</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-base text-black">
                    {account.belt ? beltLabels[account.belt as Belt] : '—'}
                  </div>
                )}
              </div>

              {/* Rank */}
              <div>
                <label
                  htmlFor="rank"
                  className="mb-1 flex items-center gap-2 text-sm font-medium text-[#666666]"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                  Rank
                </label>
                {isEditMode ? (
                  <Select
                    value={formData.rank || 'NONE'}
                    onValueChange={value =>
                      handleFieldChange('rank', value === 'NONE' ? null : value)
                    }
                  >
                    <SelectTrigger id="rank" className="max-w-md">
                      <SelectValue placeholder="Select rank" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">Not set</SelectItem>
                      <SelectItem value="0">0 stripes</SelectItem>
                      <SelectItem value="1">1 stripe</SelectItem>
                      <SelectItem value="2">2 stripes</SelectItem>
                      <SelectItem value="3">3 stripes</SelectItem>
                      <SelectItem value="4">4 stripes</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-base text-black">
                    {account.belt ? rankLabel : '—'}
                  </div>
                )}
              </div>

              {/* Empty State for BJJ Info */}
              {!account.belt && !isEditMode && (
                <div className="mt-4 rounded-xl border border-dashed border-[#0033A0]/30 bg-[#0033A0]/5 p-6 text-center">
                  <svg
                    className="mx-auto mb-3 h-12 w-12 text-[#0033A0]/50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  <p className="mb-2 text-sm font-semibold text-black">
                    Set Your Belt Rank
                  </p>
                  <p className="mb-4 text-xs text-[#666666]">
                    Track your BJJ progression journey
                  </p>
                  <Button
                    size="sm"
                    onClick={handleEdit}
                    className="bg-[#0033A0] hover:bg-[#002080]"
                  >
                    Add Belt Rank
                  </Button>
                </div>
              )}
            </div>

            {/* Belt Visualizer */}
            <div className="flex items-center justify-center">
              <BeltVisualizer
                belt={account.belt as Belt | null}
                rank={account.rank}
              />
            </div>
          </div>
        </div>

        {/* Account Metadata Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <svg
                className="h-5 w-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-black">
              Account Details
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Account Created */}
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm font-medium text-[#666666]">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Account Created
              </label>
              <div className="text-base text-black">
                {new Date(account.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>

            {/* Last Updated */}
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm font-medium text-[#666666]">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Last Updated
              </label>
              <div className="text-base text-black">
                {new Date(account.updatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
